// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A timeline event component for displaying asset ownership changes.
 * This module provides a visual representation of ownership transfer
 * events in an asset's history, including details like sale price,
 * date, and owner information. It supports editing through a dialog.
 *
 * Key features:
 * - Ownership event display
 * - Sale price formatting
 * - Owner information
 * - Edit capability
 * - Tooltip details
 */
import { AssetOwnershipEvent } from '@/asset/api/types';
import { AssetTimelineEvent } from '@/asset/components/timeline-event';
import { UpdateAssetOwnershipDialog } from '@/asset/components/update-asset-ownership-event-dialog';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { useDisclosure } from '@/hooks/use-disclosure';
import { Stack, Typography } from '@mui/material';

/**
 * Props for configuring the AssetOwnershipTimelineEvent component.
 *
 * @interface Props
 */
interface Props {
  /**
   * Unique identifier for the asset.
   * Used for editing the ownership event.
   */
  assetKey: string;

  /**
   * The asset's own currency, which denominates the sale price.
   */
  costCurrencyCode: IsoCurrencyCode;

  /**
   * The ownership event details to display.
   * Contains information about the transfer of ownership.
   */
  event: AssetOwnershipEvent;
}

/**
 * A timeline event component for asset ownership changes.
 * Displays ownership transfer details including the date,
 * owner name, and sale price. Provides an edit dialog for
 * modifying event details.
 *
 * Features:
 * - Ownership event display
 * - Sale price formatting
 * - Owner information display
 * - Edit capability via dialog
 * - Detailed tooltip
 *
 * Usage example:
 * ```tsx
 * function AssetTimeline() {
 *   const ownershipEvent: AssetOwnershipEvent = {
 *     date: new Date(),
 *     customer: { id: '123', name: 'Acme Corp' },
 *     salePrice: 1000000
 *   };
 *
 *   return (
 *     <AssetOwnershipTimelineEvent
 *       assetKey="ASSET-123"
 *       event={ownershipEvent}
 *     />
 *   );
 * }
 * ```
 */
export function AssetOwnershipTimelineEvent(props: Props) {
  const { user } = useCurrentUser();
  const dialog = useDisclosure();

  // Format the event date for display
  const date = props.event.date.toLocaleString({ dateStyle: 'medium' });

  // Determine the owner name - either the organization or customer
  const ownerName =
    props.event.customer === null
      ? user.organization.name
      : props.event.customer.name;

  // Format the sale price in its recorded currency, or use a placeholder if not set
  const salePrice = formatCurrencyValue(props.event.salePrice, {
    placeholder: 'Not set',
  });

  return (
    <>
      <UpdateAssetOwnershipDialog
        assetKey={props.assetKey}
        costCurrencyCode={props.costCurrencyCode}
        isOpen={dialog.isOpen}
        event={props.event}
        onClose={dialog.close}
      />
      <AssetTimelineEvent
        type="ownership"
        label="Sold"
        description={date}
        onEdit={dialog.open}
        tooltip={
          <Stack>
            <Typography variant="inherit">
              <b>Owner:</b> {ownerName}
            </Typography>
            <Typography variant="inherit">
              <b>Effective date:</b> {date}
            </Typography>
            <Typography variant="inherit">
              <b>Sale price:</b> {salePrice}
            </Typography>
          </Stack>
        }
      />
    </>
  );
}
